import { createContext, useReducer } from 'react';
import LoadingScreen from 'components/LoadingScreen';

import axiosInstance from '../utils/axios';
import { baseUrl } from 'utils/constants';
import { localSet } from 'utils/utils';
import React from 'react';

// --------------------------------------------------------

interface AuthUser {
    [key: string]: string | number | boolean | null;
}

interface PendingMfa {
    pendingMfaToken: string;
    totpEnabled: boolean;
    mfaCompleted?: boolean;
}

interface AuthState {
    isAuthenticated: boolean;
    isInitialized: boolean;
    user: AuthUser | null;
    pendingMfa: PendingMfa | null;
}

interface TotpSetupResponse {
    qrCodeUrl: string;
    secret: string;
}

interface AuthContextValue extends AuthState {
    method: string;
    logout: () => void;
    login: (email: string, password: string, fpwdid?: string) => Promise<void>;
    forgot: (email: string, password?: string) => Promise<boolean>;
    totpSetup: (pendingMfaToken: string) => Promise<TotpSetupResponse>;
    totpValidate: (pendingMfaToken: string, code: string) => Promise<void>;
}

type AuthAction =
    | { type: `INIT`; payload: { user: AuthUser | null; isAuthenticated: boolean } }
    | { type: `PENDING_MFA`; payload: { pendingMfa: PendingMfa } }
    | { type: `LOGIN` }
    | { type: `LOGOUT` }
    | { type: `REGISTER`; payload: { user: AuthUser } };

const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
    pendingMfa: null, // { pendingMfaToken, totpEnabled } -- set between password and TOTP steps
}; // const isValidToken = (accessToken: string) => {
//   if (!accessToken) return false;
//   // const decodedToken = jwtDecode<{ exp: number }>(accessToken);
//   const currentTime = Date.now() / 1000;
//   // return decodedToken.exp > currentTime;
// };
// const setSession = (accessToken: string | null) => {
//   if (accessToken) {
//     // localStorage.setItem('accessToken', accessToken);
//     axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
//   } else {
//     // localStorage.removeItem('accessToken');
//     delete axios.defaults.headers.common.Authorization;
//   }
// };

const request = <T,>(method: `get` | `post` | `put` | `delete` | `patch`, url: string, param?: Record<string, string | number | boolean>): Promise<T> => {
    return axiosInstance[method](`${baseUrl}${url}`, param).then(r => {
        return r.data as T;
    });
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case `INIT`: {
            return {
                isInitialized: true,
                user: action.payload.user,
                isAuthenticated: action.payload.isAuthenticated,
                pendingMfa: null,
            };
        }

        case `PENDING_MFA`: {
            return { ...state, pendingMfa: action.payload.pendingMfa };
        }

        case `LOGIN`: {
            return { ...state, pendingMfa: null };
        }

        case `LOGOUT`: {
            return { ...state, user: null, isAuthenticated: false };
        }

        case `REGISTER`: {
            return { ...state, isAuthenticated: true, user: action.payload.user };
        }

        default: {
            return state;
        }
    }
};

const AuthContext = createContext<AuthContextValue>({
    ...initialState,
    method: `JWT`,
    logout: () => {},
    login: () => Promise.resolve(),
    forgot: () => Promise.resolve(true),
    totpSetup: () => Promise.resolve({ qrCodeUrl: ``, secret: `` }),
    totpValidate: () => Promise.resolve(),
});
export const JWTAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const login = (email: string, password: string, fpwdid?: string): Promise<void> => {
        return window.grecaptcha
            .execute(`6LesMmYbAAAAACONR1kp2flqwlel9cK6gJXDvbkx`, { action: `login` })
            .then(recaptchaToken => {
                return request<PendingMfa>(`post`, `login`, {
                    email,
                    password,
                    recaptchaToken,
                    ...(fpwdid && { forgot: fpwdid }),
                }).then(({ pendingMfaToken, totpEnabled }) => {
                    dispatch({
                        type: `PENDING_MFA`,
                        payload: { pendingMfa: { pendingMfaToken, totpEnabled } },
                    });
                });
            });
    };

    // Fetches QR code for first-time TOTP setup
    const totpSetup = (pendingMfaToken: string): Promise<TotpSetupResponse> => request<TotpSetupResponse>(`post`, `totp/setup`, { pendingMfaToken });

    // Validates the 6-digit code; on success stores the full sid
    const totpValidate = (pendingMfaToken: string, code: string): Promise<void> =>
        request<{ sid: string }>(`post`, `totp/validate`, { pendingMfaToken, code }).then(({ sid: newSid }) => {
            localSet(`sid`, newSid);
            dispatch({
                type: `LOGIN`,
            });
        });

    const logout = (): void => {
        // setSession(null);
        dispatch({
            type: `LOGOUT`,
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const forgot = (email: string, _password?: string): Promise<boolean> =>
        window.grecaptcha
            .execute(`6LesMmYbAAAAACONR1kp2flqwlel9cK6gJXDvbkx`, { action: `login` })
            .then(recaptchaToken => {
                return request<void>(`post`, `login`, {
                    email,
                    forgot: 1,
                    recaptchaToken,
                }).then(() => true);
            });

    if (!state.isInitialized) <LoadingScreen />;
    return (
        <AuthContext.Provider value={{ ...state, method: `JWT`, login, forgot, logout, totpSetup, totpValidate }}>
            {children}
        </AuthContext.Provider>
    );
};
export default AuthContext;
